import type { Note } from "@hq/contracts";
import { descendantFolderIds, hasTag, type NoteSort, sortNotes } from "@hq/core";
import { useFolders, useLiveQuery, useNotes, useNowContext, useServices } from "@hq/client-react";
import { MarkdownThumbnail } from "@hq/markdown-editor";
import type { NoteViewMode } from "./useNoteViewMode";
import type { SearchResult } from "@hq/local-store";
import {
  useDeleteConfirmation,
  Badge,
  cn,
  ContextMenu,
  EmptyState,
  IconButton,
  Input,
  RenameInput,
  toast,
  Tooltip,
} from "@z/ui";
import { FileText, Pin, Plus, Search, X } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { FolderPickerDialog } from "./FolderPickerDialog";
import { displayTitle, effectiveFolderId, NOTE_DRAG_MIME, notePreview, relativeTime } from "./lib";

export type NoteListProps = {
  /** `null` lists every note ("All notes"); an id lists that folder. */
  folderId: string | null;
  selectedNoteId: string | null;
  onSelect: (noteId: string) => void;
  /** Enter on a row. Falls back to `onSelect`. */
  onOpen?: (noteId: string) => void;
  sort?: NoteSort;
  viewMode?: NoteViewMode;
  showCreate?: boolean;
  renderAction?: (note: Note) => ReactNode;
  /** Also list notes in subfolders of `folderId`. */
  includeSubfolders?: boolean;
  /** Only notes carrying this tag (case-insensitive). */
  tag?: string;
  className?: string;
};

/** How many tag badges a row shows before collapsing the rest into "+N". */
const ROW_TAG_LIMIT = 3;

/** Renders `[hit]` markers from `SearchResult.snippet` as highlighted text. */
export function renderSnippet(snippet: string): ReactNode[] {
  return snippet.split(/(\[[^\]]+\])/).map((part, i) =>
    part.startsWith("[") && part.endsWith("]") ? (
      // Segment order is stable for one snippet, so the index is a fine key.
      <mark key={i} className="rounded-sm bg-accent-muted text-accent">
        {part.slice(1, -1)}
      </mark>
    ) : (
      part
    ),
  );
}

type Row = { note: Note; snippet?: string };

/**
 * The note list for a folder: pinned first, then newest. Up/Down move the selection, Enter
 * opens, Backspace/Delete moves to Trash with an Undo toast, `p` toggles pinned. Rows are
 * drag sources (`NOTE_DRAG_MIME`) the FolderTree accepts. A non-empty search box switches
 * the list to full-text results with snippets, still scoped to the folder.
 */
export function NoteList({
  folderId,
  selectedNoteId,
  onSelect,
  onOpen,
  sort = "updatedAt",
  viewMode = "list",
  showCreate = true,
  renderAction,
  includeSubfolders = false,
  tag,
  className,
}: NoteListProps) {
  const services = useServices();
  const { requestDelete, deleteDialog } = useDeleteConfirmation();
  const now = useNowContext();
  const notes = useNotes().data ?? [];
  const folders = useFolders().data ?? [];
  const [query, setQuery] = useState("");
  const q = query.trim();
  const search = useLiveQuery(
    ["notes"],
    (s): Promise<SearchResult[]> => (q ? s.search.notes(q, { limit: 100 }) : Promise.resolve([])),
    [q, folderId],
  );
  const menuHandoff = useRef(false);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClick = () => {
    if (clickTimer.current !== null) clearTimeout(clickTimer.current);
    clickTimer.current = null;
  };
  useEffect(
    () => () => {
      if (clickTimer.current !== null) clearTimeout(clickTimer.current);
    },
    [],
  );
  const [movingId, setMovingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const scope =
    folderId === null
      ? null
      : includeSubfolders
        ? descendantFolderIds(folders, folderId)
        : new Set([folderId]);
  const inScope = notes.filter((n) => {
    if (tag !== undefined && !hasTag(n.tags, tag)) return false;
    if (scope === null) return true;
    const id = effectiveFolderId(n, folders);
    return id !== null && scope.has(id);
  });

  let rows: Row[];
  if (q) {
    const byId = new Map(inScope.map((n) => [n.id, n]));
    rows = [];
    for (const hit of search.data ?? []) {
      const note = byId.get(hit.id);
      if (note) rows.push({ note, snippet: hit.snippet });
    }
  } else {
    rows = sortNotes(inScope, sort).map((note) => ({ note }));
  }

  const focusRow = (id: string) => {
    onSelect(id);
    listRef.current?.querySelector<HTMLElement>(`[data-note-id="${id}"]`)?.focus();
  };

  const createNote = async () => {
    const note = await services.notes.create(folderId ? { folderId } : {});
    onSelect(note.id);
  };

  const trash = (note: Note) => {
    requestDelete({
      name: displayTitle(note),
      description: "You can restore this file from Trash.",
      onConfirm: async () => {
        const index = rows.findIndex((r) => r.note.id === note.id);
        const neighbour = rows[index + 1] ?? rows[index - 1];
        await services.notes.delete(note.id);
        if (neighbour) focusRow(neighbour.note.id);
        toast({
          title: `Moved "${displayTitle(note)}" to Trash`,
          action: { label: "Undo", onClick: () => void services.notes.restore(note.id) },
        });
      },
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLInputElement) return;
    cancelClick();
    const focusedId = (e.target as HTMLElement).closest<HTMLElement>("[data-note-id]")?.dataset
      .noteId;
    const index = rows.findIndex((r) => r.note.id === (focusedId ?? selectedNoteId));
    if (
      viewMode === "gallery" &&
      ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)
    ) {
      e.preventDefault();
      const cards = Array.from(
        listRef.current?.querySelectorAll<HTMLElement>("[data-note-id]") ?? [],
      );
      const columns = cards.filter((card) => card.offsetTop === cards[0]?.offsetTop).length || 1;
      const step =
        e.key === "ArrowDown"
          ? columns
          : e.key === "ArrowUp"
            ? -columns
            : e.key === "ArrowLeft"
              ? -1
              : 1;
      const next =
        e.key === "Home"
          ? 0
          : e.key === "End"
            ? rows.length - 1
            : Math.max(0, Math.min(rows.length - 1, index + step));
      cards[next]?.focus();
      return;
    }
    const selected = index >= 0 ? rows[index]?.note : undefined;
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = rows[Math.min(index + 1, rows.length - 1)];
        if (next) focusRow(next.note.id);
        return;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = rows[Math.max(index - 1, 0)];
        if (prev) focusRow(prev.note.id);
        return;
      }
      case "F2":
        if (selected) {
          e.preventDefault();
          setRenamingId(selected.id);
        }
        return;
      case "Enter":
        if (selected) {
          e.preventDefault();
          (onOpen ?? onSelect)(selected.id);
        }
        return;
      case "Backspace":
      case "Delete":
        if (selected) {
          e.preventDefault();
          void trash(selected);
        }
        return;
      case "p":
        if (selected && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          void services.notes.setPinned(selected.id, !selected.pinned);
        }
        return;
      default:
    }
  };

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div className="flex items-center gap-2 border-b border-border/60 px-5 py-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-faint" />
          <Input
            type="search"
            aria-label={folderId ? "Search this folder" : "Search all notes"}
            placeholder={folderId ? "Search this folder" : "Search all notes"}
            value={query}
            className="h-8 bg-surface/50 pl-7"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape" && query) {
                e.preventDefault();
                setQuery("");
              }
            }}
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded-sm text-fg-faint hover:text-fg"
              onClick={() => setQuery("")}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        {showCreate && (
          <Tooltip content="New note" shortcutId="notes.new">
            <IconButton label="New note" size="sm" onClick={() => void createNote()}>
              <Plus />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div
        ref={listRef}
        role="listbox"
        aria-label="Notes"
        data-view={viewMode}
        className={cn(
          "min-h-0 flex-1 overflow-y-auto",
          viewMode === "gallery"
            ? "grid content-start grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] gap-5 bg-surface/30 p-5 sm:p-6"
            : "flex flex-col gap-1 p-3",
        )}
        onKeyDown={onKeyDown}
      >
        {rows.length === 0 && (
          <EmptyState
            className="col-span-full"
            icon={<FileText />}
            title={q ? "No matches" : tag !== undefined ? "No notes with this tag" : "No notes yet"}
            description={
              q
                ? `Nothing here matches "${q}".`
                : tag !== undefined
                  ? `Nothing here is tagged "${tag}".`
                  : "Use the plus button to start a note."
            }
          />
        )}
        {rows.map(({ note, snippet }) => {
          const selected = note.id === selectedNoteId;
          return (
            <ContextMenu.Root
              key={note.id}
              disabled={renamingId === note.id}
              onOpenChange={(open) => {
                if (open) {
                  cancelClick();
                  menuHandoff.current = false;
                }
              }}
            >
              <ContextMenu.Trigger
                render={
                  <div
                    role="option"
                    aria-selected={selected}
                    {...(viewMode === "gallery" ? { "aria-label": displayTitle(note) } : {})}
                    data-note-id={note.id}
                    tabIndex={
                      selected ||
                      (!rows.some((r) => r.note.id === selectedNoteId) &&
                        rows[0]?.note.id === note.id)
                        ? 0
                        : -1
                    }
                    draggable={renamingId !== note.id}
                    className={cn(
                      "group flex min-w-0 cursor-pointer flex-col outline-none transition-colors duration-fast select-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset",
                      viewMode === "gallery"
                        ? "h-[340px] gap-3 overflow-hidden rounded-lg border border-border/70 bg-bg p-5 shadow-sm hover:border-accent/40 hover:bg-surface/40"
                        : "gap-1.5 rounded-md px-3 py-3 hover:bg-fg/6",
                      selected &&
                        (viewMode === "gallery"
                          ? "border-accent/60 ring-2 ring-accent/20"
                          : "bg-accent-muted hover:bg-accent-muted"),
                    )}
                    onClick={(event) => {
                      cancelClick();
                      if (event.detail > 1) return;
                      // Opening a note can unmount this list; wait briefly for a second click.
                      clickTimer.current = setTimeout(() => {
                        clickTimer.current = null;
                        onSelect(note.id);
                      }, 300);
                    }}
                    onDoubleClick={() => {
                      cancelClick();
                      setRenamingId(note.id);
                    }}
                    onDragStart={(e) => {
                      cancelClick();
                      e.dataTransfer.setData(NOTE_DRAG_MIME, note.id);
                      e.dataTransfer.setData("text/plain", displayTitle(note));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                  >
                    <div className="flex items-center gap-1.5">
                      {note.pinned && (
                        <Pin aria-label="Pinned" className="size-3 shrink-0 text-accent" />
                      )}
                      {renamingId === note.id ? (
                        <RenameInput
                          initial={displayTitle(note)}
                          label={`Rename ${displayTitle(note)}`}
                          onSave={(title) => services.notes.rename(note.id, title)}
                          onClose={() => {
                            setRenamingId(null);
                            listRef.current
                              ?.querySelector<HTMLElement>(`[data-note-id="${note.id}"]`)
                              ?.focus();
                          }}
                          className="h-7 min-w-0 flex-1"
                        />
                      ) : (
                        <span
                          className={cn(
                            "flex-1 truncate font-medium text-fg",
                            viewMode === "gallery" ? "text-lg" : "text-base",
                            selected && "text-accent",
                          )}
                        >
                          {displayTitle(note)}
                        </span>
                      )}
                      {viewMode === "list" && (
                        <time
                          dateTime={note.updatedAt}
                          className="shrink-0 text-xs tabular-nums text-fg-faint"
                        >
                          {relativeTime(note.updatedAt, now)}
                        </time>
                      )}
                    </div>
                    {renderAction && (
                      <div
                        onClick={(event) => event.stopPropagation()}
                        onDoubleClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        {renderAction(note)}
                      </div>
                    )}
                    {viewMode === "gallery" && (
                      <div className="min-h-0 flex-1 overflow-hidden border-t border-border/50 pt-3">
                        <MarkdownThumbnail markdown={note.markdown} />
                      </div>
                    )}
                    {(viewMode === "list" || snippet !== undefined) && (
                      <p className="truncate text-xs text-fg-muted">
                        {snippet !== undefined
                          ? renderSnippet(snippet)
                          : notePreview(note.markdown) || " "}
                      </p>
                    )}
                    {note.tags !== undefined && note.tags.length > 0 && (
                      <div
                        data-slot="note-row-tags"
                        className="flex items-center gap-1 overflow-hidden"
                      >
                        {note.tags.slice(0, ROW_TAG_LIMIT).map((t) => (
                          <Badge
                            key={t}
                            data-slot="note-tag"
                            className="h-4 border-transparent bg-surface-raised px-1 font-normal text-fg-faint"
                          >
                            {t}
                          </Badge>
                        ))}
                        {note.tags.length > ROW_TAG_LIMIT && (
                          <span className="text-[11px] text-fg-faint">
                            +{note.tags.length - ROW_TAG_LIMIT}
                          </span>
                        )}
                      </div>
                    )}
                    {viewMode === "gallery" && (
                      <div className="flex min-w-0 items-center justify-between gap-2 border-t border-border/50 pt-3 text-[11px] text-fg-faint">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <FileText className="size-3 shrink-0" />
                          <span className="truncate">
                            {folders.find((folder) => folder.id === note.folderId)?.name ?? "Notes"}
                          </span>
                        </span>
                        <time className="shrink-0" dateTime={note.updatedAt}>
                          {relativeTime(note.updatedAt, now)}
                        </time>
                      </div>
                    )}
                  </div>
                }
              />
              <ContextMenu.Content
                aria-label={`Note actions for ${displayTitle(note)}`}
                finalFocus={() =>
                  menuHandoff.current
                    ? false
                    : (listRef.current?.querySelector<HTMLElement>(`[data-note-id="${note.id}"]`) ??
                      false)
                }
              >
                <ContextMenu.Item onClick={() => (onOpen ?? onSelect)(note.id)}>
                  Open note
                </ContextMenu.Item>
                <ContextMenu.Item
                  onClick={() => {
                    menuHandoff.current = true;
                    // Mount the editor after the menu has released its focus scope.
                    requestAnimationFrame(() => setRenamingId(note.id));
                  }}
                >
                  Rename
                  <ContextMenu.Shortcut keys="F2" />
                </ContextMenu.Item>
                <ContextMenu.Item
                  onClick={() => void services.notes.setPinned(note.id, !note.pinned)}
                >
                  {note.pinned ? "Unpin note" : "Pin note"}
                </ContextMenu.Item>
                <ContextMenu.Item
                  onClick={() => {
                    menuHandoff.current = true;
                    setMovingId(note.id);
                  }}
                >
                  Move to folder…
                </ContextMenu.Item>
                <ContextMenu.Separator />
                <ContextMenu.Item
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(note.markdown)
                      .then(() => toast({ title: "Copied as Markdown" }))
                      .catch(() => toast({ title: "Could not copy note", variant: "danger" }));
                  }}
                >
                  Copy as Markdown
                </ContextMenu.Item>
                <ContextMenu.Separator />
                <ContextMenu.Item variant="destructive" onClick={() => void trash(note)}>
                  Move to Trash
                </ContextMenu.Item>
              </ContextMenu.Content>
            </ContextMenu.Root>
          );
        })}
      </div>
      {movingId && (
        <FolderPickerDialog
          open
          onOpenChange={(open) => {
            if (!open) setMovingId(null);
          }}
          title="Move note"
          currentId={notes.find((note) => note.id === movingId)?.folderId ?? null}
          onPick={(destinationId) => void services.notes.move(movingId, destinationId)}
        />
      )}
      {deleteDialog}
    </div>
  );
}
